import { Search } from "lucide-react";
import type { ReactNode } from "react";

/**
 * The filter strip that sits between a list card's edge and its table.
 *
 * Every list screen had grown its own version of this out of `TextInput` and
 * `SelectField`, which are *form* controls: each one stacked a label above a
 * full-width box, so a single search field spent ~110px of vertical height
 * announcing the word "Search" above a placeholder that already said it. On a
 * page that must fit the viewport exactly (D-15) that height comes straight
 * out of the rows.
 *
 * Controls wrap into readable rows as space narrows, without hiding filters
 * behind a horizontal scrollbar.
 */
export function Toolbar({ children, trailing }: { children: ReactNode; trailing?: ReactNode }) {
  return (
    <div className="flex min-w-0 shrink-0 flex-col gap-2.5 border-b border-rule/70 px-3 py-3 sm:px-5 lg:flex-row lg:items-start">
      <div className="flex w-full min-w-0 flex-wrap items-end gap-2.5 py-0.5 lg:flex-1 [&>button]:whitespace-nowrap">{children}</div>
      {trailing ? <div className="flex w-full flex-wrap items-center justify-end gap-2.5 lg:ml-auto lg:w-auto lg:shrink-0">{trailing}</div> : null}
    </div>
  );
}

/** The row count that sits at the right of a toolbar. */
export function ToolbarCount({ count, noun }: { count: number; noun: string }) {
  return (
    <p className="rounded-full bg-ink/[0.04] px-2.5 py-1 text-xs font-medium tabular-nums text-muted">
      {count} {count === 1 ? noun : /(s|x|ch|sh)$/.test(noun) ? `${noun}es` : `${noun}s`}
    </p>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = "Search…",
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Announced to screen readers in place of a visible label. */
  label: string;
}) {
  return (
    <div className="relative w-full min-w-0 sm:w-56 sm:shrink-0">
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
      />
      <input
        aria-label={label}
        className="h-9 w-full rounded-md border border-rule bg-raised pr-3 pl-9 text-sm text-ink shadow-control transition-[border-color,box-shadow] placeholder:text-muted/60 hover:border-ink/25 focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none"
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type="search"
        value={value}
      />
    </div>
  );
}

/**
 * Each filter takes half a narrow row and up to a quarter of a wide row.
 * The stacked label leaves room for longer values without clipping controls.
 */
export function FilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel = "All",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  /** The empty-value option. Pass `null` to omit it. */
  allLabel?: string | null;
}) {
  return (
    <label className="flex w-[calc(50%-0.3125rem)] min-w-0 flex-col gap-1 text-xs text-muted sm:w-[calc(33.333%-0.5rem)] xl:w-[calc(25%-0.5rem)]">
      <span className="break-words leading-4">{label}</span>
      <select
        className="h-9 w-full min-w-0 truncate rounded-md border border-rule bg-raised px-2.5 text-sm text-ink shadow-control transition-[border-color,box-shadow] hover:border-ink/25 focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none"
        title={options.find((option) => option.value === value)?.label ?? allLabel ?? label}
        onChange={(e) => onChange(e.target.value)}
        value={value}
      >
        {allLabel === null ? null : <option value="">{allLabel}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
